import { Injectable } from '@angular/core';
import { metrics } from '@opentelemetry/api';
import { OTLPMetricExporter } from '@opentelemetry/exporter-metrics-otlp-http';
import { resourceFromAttributes } from '@opentelemetry/resources';
import { MeterProvider, PeriodicExportingMetricReader } from '@opentelemetry/sdk-metrics';
import { ATTR_SERVICE_NAME, ATTR_SERVICE_VERSION } from '@opentelemetry/semantic-conventions';

import { environment } from '@ninety/ui/web/environments';

@Injectable({ providedIn: 'root' })
export class OtelMeterService {
  private meterProvider: MeterProvider;

  constructor() {
    const resource = resourceFromAttributes({
      [ATTR_SERVICE_NAME]: environment.service,
      [ATTR_SERVICE_VERSION]: environment.appVersion,
      ['host.name']: window.location.hostname,
      env: environment.env,
    });

    const readers = [];
    if (environment?.openTelemetryCollectorOrigin && environment.production) {
      readers.push(
        new PeriodicExportingMetricReader({
          exporter: new OTLPMetricExporter({
            url: environment.openTelemetryCollectorOrigin + '/v1/metrics',
            headers: {},
            timeoutMillis: 10000,
          }),
        })
      );
    }
    // //Uncomment for console output
    // readers.push(
    //   new PeriodicExportingMetricReader({
    //     exporter: new ConsoleMetricExporter(),
    //     exportIntervalMillis: 10000,
    //   })
    // );

    this.meterProvider = new MeterProvider({
      resource,
      readers,
    });
    metrics.setGlobalMeterProvider(this.meterProvider);
  }

  getMeter(name, version?: string) {
    return metrics.getMeter(name, version);
  }

  /**
   * Returns a Counter instrument for the given name and options.
   */
  getCounter(name: string, options?: Parameters<ReturnType<typeof metrics.getMeter>['createCounter']>[1]) {
    return this.getMeter(name).createCounter(name, options);
  }

  /**
   * Returns an UpDownCounter instrument for the given name and options.
   */
  getUpDownCounter(name: string, options?: Parameters<ReturnType<typeof metrics.getMeter>['createUpDownCounter']>[1]) {
    return this.getMeter(name).createUpDownCounter(name, options);
  }

  /**
   * Returns a Histogram instrument for the given name and options.
   */
  getHistogram(name: string, options?: Parameters<ReturnType<typeof metrics.getMeter>['createHistogram']>[1]) {
    return this.getMeter(name).createHistogram(name, options);
  }

  /**
   * Returns an ObservableGauge instrument for the given name and options.
   */
  getObservableGauge(
    name: string,
    options?: Parameters<ReturnType<typeof metrics.getMeter>['createObservableGauge']>[1]
  ) {
    return this.getMeter(name).createObservableGauge(name, options);
  }

  /**
   * Returns an ObservableCounter instrument for the given name and options.
   */
  getObservableCounter(
    name: string,
    options?: Parameters<ReturnType<typeof metrics.getMeter>['createObservableCounter']>[1]
  ) {
    return this.getMeter(name).createObservableCounter(name, options);
  }

  /**
   * Returns an ObservableUpDownCounter instrument for the given name and options.
   */
  getObservableUpDownCounter(
    name: string,
    options?: Parameters<ReturnType<typeof metrics.getMeter>['createObservableUpDownCounter']>[1]
  ) {
    return this.getMeter(name).createObservableUpDownCounter(name, options);
  }
}
